// Fences: the trameria fence node, and the CommonMark fence rule other line
// scanners share.
//
// A fenced block whose info string is exactly one of TRAMERIA_FENCE_KINDS
// (`mermaid`) is a `trameriaFence` node: `attrs.kind` says which, and
// `attrs.payload` holds the body as written (interpreted by the screen's
// renderer). Any other info string, or none, stays a plain `codeBlock`.
//
// A dedicated block tokenizer diverts the reserved info strings before marked's
// fence tokenizer sees them (`@tiptap/markdown` registers extension tokenizers
// with `marked.use()`, which runs them first).

import { Node } from "@tiptap/core";
import { blockIdDomAttrs } from "./block-id";

export const TRAMERIA_FENCE_KINDS = ["mermaid"] as const;
export type TrameriaFenceKind = (typeof TRAMERIA_FENCE_KINDS)[number];

const KINDS_SRC = TRAMERIA_FENCE_KINDS.join("|");
const START_RE = new RegExp(`^\`\`\`(${KINDS_SRC})\\n`, "m");
const TOKEN_RE = new RegExp(
  `^\`\`\`(${KINDS_SRC})\\n([\\s\\S]*?)\\n\`\`\`(?:\\n|$)`,
);

export const TrameriaFence = Node.create({
  name: "trameriaFence",
  group: "block",
  atom: true,
  selectable: true,

  addAttributes() {
    return {
      kind: { default: "mermaid" as TrameriaFenceKind },
      payload: { default: "" },
    };
  },

  parseHTML() {
    return [{ tag: "pre[data-trameria-fence]" }];
  },

  // Returns its own attributes only (not the merged `HTMLAttributes`), so the
  // block id is written explicitly.
  renderHTML({ node }) {
    return [
      "pre",
      { "data-trameria-fence": node.attrs?.kind, ...blockIdDomAttrs(node) },
      node.attrs?.payload,
    ];
  },

  markdownTokenName: "trameriaFence",
  markdownTokenizer: {
    name: "trameriaFence",
    level: "block",
    start(src: string) {
      const match = src.match(START_RE);
      return match ? (match.index ?? -1) : -1;
    },
    tokenize(src: string) {
      const match = TOKEN_RE.exec(src);
      if (!match) return undefined;
      return {
        type: "trameriaFence",
        raw: match[0],
        kind: match[1],
        payload: match[2],
      };
    },
  },
  parseMarkdown(token, h) {
    return h.createNode(
      "trameriaFence",
      { kind: token.kind, payload: token.payload },
      [],
    );
  },
  renderMarkdown(node) {
    return `\`\`\`${node.attrs?.kind}\n${node.attrs?.payload}\n\`\`\``;
  },
});

/** What a fence scanner remembers about the open fence. */
export interface FenceOpenState {
  markerChar: "`" | "~";
  length: number;
}

/**
 * Whether `line` opens a fence (0-3 spaces, then 3 or more backticks or
 * tildes). Called while not inside a fence.
 */
export function matchFenceOpen(line: string): FenceOpenState | undefined {
  const match = /^ {0,3}(`{3,}|~{3,})/.exec(line);
  if (!match) return undefined;
  return { markerChar: match[1][0] as "`" | "~", length: match[1].length };
}

/**
 * Whether `line` closes the fence `open`. CommonMark: only a line of the same
 * character, at least as long, closes it - which is what keeps a three-backtick
 * example inside a four-backtick fence inert.
 */
export function matchFenceClose(open: FenceOpenState, line: string): boolean {
  const trimmed = line.trimEnd();
  return (
    trimmed.length >= open.length &&
    [...trimmed].every((ch) => ch === open.markerChar)
  );
}
